import React, { useState, useEffect } from 'react';
import { OrganizationsAPI } from '../../Services/PharmacyAPI';
import DataTable from 'react-data-table-component';
import SpinnerModal from '../../ShareComponent/Spinner/SpinnerModal';
import { toast } from 'react-toastify';
import CommonModal from '../Common/CommonModal';
import '../Common/common.css';

const OrganizationManagement = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // create, edit
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    licenseNumber: '',
    contactPerson: '',
    isActive: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setIsLoading(true);
      const data = await OrganizationsAPI.getAllOrganizations();
      setOrganizations(data || []);
    } catch (error) {
      console.error('Error loading organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setSelectedItem(null);
    resetFormData();
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setModalMode('edit');
    setSelectedItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      address: item.address || '',
      phone: item.phone || '',
      email: item.email || '',
      website: item.website || '',
      licenseNumber: item.licenseNumber || '',
      contactPerson: item.contactPerson || '',
      isActive: item.isActive !== undefined ? item.isActive : true
    });
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete organization "${item.name}"?`)) {
      return;
    }

    try {
      setIsLoading(true);
      await OrganizationsAPI.deleteOrganization(item.organizationID);
      toast.success('Organization deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting organization:', error);
      toast.error('Failed to delete organization');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      
      if (modalMode === 'create') {
        await OrganizationsAPI.createOrganization(formData);
        toast.success('Organization created successfully');
      } else {
        await OrganizationsAPI.updateOrganization(selectedItem.organizationID, formData);
        toast.success('Organization updated successfully');
      }
      
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving organization:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save organization';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const resetFormData = () => {
    setFormData({
      name: '',
      description: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      licenseNumber: '',
      contactPerson: '',
      isActive: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const columns = [
    {
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      wrap: true
    },
    {
      name: 'Email',
      selector: row => row.email || '-',
      sortable: true,
      wrap: true
    },
    {
      name: 'Phone',
      selector: row => row.phone || '-',
      sortable: true,
      wrap: true
    },
    {
      name: 'Contact Person',
      selector: row => row.contactPerson || '-',
      sortable: true,
      wrap: true
    },
    {
      name: 'Status',
      selector: row => row.isActive ? 'Active' : 'Inactive',
      sortable: true,
      cell: row => (
        <span className={`badge ${row.isActive ? 'bg-success' : 'bg-secondary'}`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="action-buttons">
          <button
            className="btn btn-sm btn-primary me-2"
            onClick={() => handleEdit(row)}
            title="Edit"
          >
            <i className="bi bi-pencil"></i> Edit
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => handleDelete(row)}
            title="Delete"
          >
            <i className="bi bi-trash"></i> Delete
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true
    }
  ];

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Organization Management</h2>
        <button className="btn btn-primary" onClick={handleCreate}>
          <i className="bi bi-plus-circle"></i> Add New Organization
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          <DataTable
            columns={columns}
            data={organizations}
            progressPending={loading}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 20, 30, 50]}
            highlightOnHover
            pointerOnHover
            responsive
            striped
          />
        </div>
      </div>

      <CommonModal
        show={showModal}
        onHide={() => setShowModal(false)}
        title={modalMode === 'create' ? 'Create Organization' : 'Edit Organization'}
        onSave={handleSave}
        onCancel={() => setShowModal(false)}
        size="xl"
        scrollable
      >
        <div className="modal-form">
          <div className="form-row">
            <div className="form-group col-md-6">
              <label htmlFor="name">Organization Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group col-md-6">
              <label htmlFor="contactPerson">Contact Person</label>
              <input
                type="text"
                className="form-control"
                id="contactPerson"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group col-md-6">
              <label htmlFor="phone">Phone</label>
              <input
                type="text"
                className="form-control"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label htmlFor="website">Website</label>
              <input
                type="url"
                className="form-control"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group col-md-6">
              <label htmlFor="licenseNumber">License Number</label>
              <input
                type="text"
                className="form-control"
                id="licenseNumber"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-12">
              <label htmlFor="address">Address</label>
              <textarea
                className="form-control"
                id="address"
                name="address"
                rows="2"
                value={formData.address}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-12">
              <label htmlFor="description">Description</label>
              <textarea
                className="form-control"
                id="description"
                name="description"
                rows="3"
                value={formData.description}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                <label className="form-check-label" htmlFor="isActive">
                  Active
                </label>
              </div>
            </div>
          </div>
        </div>
      </CommonModal>

      <SpinnerModal show={isLoading} />
    </div>
  );
};

export default OrganizationManagement;

